import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { senderEmailService, regionService, branchService } from '../services/api';
import { Table, Pagination, StatusToggle, PageHeader, useToast, Card } from './common';
import { usePageTitle } from '../context/PageTitleContext';
import { useSidebar } from '../context/SidebarContext';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import usePageState from '../hooks/usePageState';
import { useAuth } from '../context/AuthContext';

const SenderEmailManager = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { getPagePermissions } = useAuth();
  const permissions = getPagePermissions('/admin/sender-emails');
  const { setPageTitle } = usePageTitle();
  const { isCollapsed } = useSidebar();

  const [senderEmails, setSenderEmails] = useState([]);
  const [regions, setRegions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 30
  });

  const [filters, setFilters] = usePageState('senderEmailManager', {
    currentPage: 1,
    itemsPerPage: 30,
    searchTerm: '',
    regionFilter: '',
    branchFilter: '',
    statusFilter: '',
    startDate: '',
    endDate: '',
  }, ['/admin/sender-emails']);
  const { currentPage, itemsPerPage, searchTerm, regionFilter, branchFilter, statusFilter, startDate, endDate } = filters;
  const setCurrentPage   = (v) => setFilters({ currentPage: v });
  const setItemsPerPage  = (v) => setFilters({ itemsPerPage: v });
  const setSearchTerm    = (v) => setFilters({ searchTerm: v });
  const setRegionFilter  = (v) => setFilters({ regionFilter: v, branchFilter: '', currentPage: 1 });
  const setBranchFilter  = (v) => setFilters({ branchFilter: v, currentPage: 1 });
  const setStatusFilter  = (v) => setFilters({ statusFilter: v, currentPage: 1 });

  useEffect(() => {
    setPageTitle('Sender Addresses');
  }, [setPageTitle]);

  // Load all active regions once
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const response = await regionService.getAll({ noPagination: true });
        const rows = response.data.data || response.data || [];
        setRegions(rows.map(r => ({ value: r.code, label: r.name })));
      } catch (err) {
        console.error('Failed to load regions', err);
      }
    };
    loadRegions();
  }, []);

  // Load branches — all on mount, filtered when a region is selected
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const params = { noPagination: true };
        if (regionFilter) params.region_codes = regionFilter;
        const response = await branchService.getAll(params);
        const rows = response.data.data || [];
        setBranches(rows.map(b => ({ value: b.code, label: b.name })));
      } catch (err) {
        console.error('Failed to load branches', err);
        setBranches([]);
      }
    };
    loadBranches();
  }, [regionFilter]);

  useEffect(() => {
    fetchSenderEmails();
  }, [currentPage, itemsPerPage, searchTerm, regionFilter, branchFilter, statusFilter, startDate, endDate]);

  const fetchSenderEmails = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: itemsPerPage };
      if (searchTerm) params.search = searchTerm;
      if (regionFilter) params.region_code = regionFilter;
      if (branchFilter) params.branch_code = branchFilter;
      if (statusFilter !== '') params.status = statusFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await senderEmailService.getAll(params);
      setSenderEmails(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Failed to fetch sender addresses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleView = (row) => navigate(`/admin/sender-emails/view/${row.id}`);
  const handleEdit = (row) => navigate(`/admin/sender-emails/edit/${row.id}`);
  const handleAdd = () => navigate('/admin/sender-emails/create');

  const handleDelete = async (id) => {
    const confirmed = await toast.confirm('Are you sure you want to delete this sender address?');
    if (!confirmed) return;
    try {
      await senderEmailService.delete(id);
      toast.success('Sender address deleted successfully');
      fetchSenderEmails();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to delete sender address';
      setError(msg);
      toast.error(msg);
      console.error(err);
    }
  };

  const handleStatusToggle = async (row, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [row.id]: true }));
    try {
      await senderEmailService.updateStatus(row.id, newStatus);
      toast.success(`Sender address ${newStatus ? 'activated' : 'deactivated'} successfully`);
      await fetchSenderEmails();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
      console.error(err);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [row.id]: false }));
    }
  };

  const convertToCSV = (rows) => {
    const headers = ['Email', 'Region', 'Branch', 'Status', 'Created At'];
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const body = rows.map(r => [
      escape(r.email),
      escape(r.region_full_name || r.region_name || ''),
      escape(r.branch_full_name || r.branch_name || ''),
      escape(r.status ? 'Active' : 'Inactive'),
      escape(formatDateDDMMYYYY(r.created_at)),
    ].join(','));
    const BOM = '\uFEFF';
    return BOM + [headers.join(','), ...body].join('\n');
  };

  const handleDownload = async () => {
    try {
      setDownloadLoading(true);
      const params = { limit: 'all' };
      if (searchTerm) params.search = searchTerm;
      if (regionFilter) params.region_code = regionFilter;
      if (branchFilter) params.branch_code = branchFilter;
      if (statusFilter !== '') params.status = statusFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await senderEmailService.getAll(params);
      const rows = response.data.data || [];
      if (!rows.length) {
        toast.error('No sender addresses to download');
        return;
      }
      const csv = convertToCSV(rows);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `sender_addresses_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Successfully downloaded ${rows.length} record(s)`);
    } catch (err) {
      console.error('Download failed', err);
      toast.error(err.response?.data?.error || 'Failed to download sender addresses');
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader
          addButton={permissions.can_add ? {
            label: 'Add Sender Address',
            onClick: handleAdd,
            variant: 'primary'
          } : null}
          downloadButton={permissions.can_download ? {
            label: downloadLoading ? 'Downloading...' : 'Download',
            onClick: handleDownload,
            variant: 'outline',
            className: 'border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500',
            disabled: downloadLoading || loading
          } : null}
          filters={{
            search: {
              value: searchTerm,
              onChange: handleSearchChange,
              placeholder: 'Search by email...'
            },
            dropdowns: [
              {
                value: regionFilter,
                onChange: (e) => setRegionFilter(e.target.value),
                options: [{ value: '', label: 'All Regions' }, ...regions],
                placeholder: 'All Regions',
                width: 'w-[180px]'
              },
              {
                value: branchFilter,
                onChange: (e) => setBranchFilter(e.target.value),
                options: [{ value: '', label: 'All Branches' }, ...branches],
                placeholder: 'All Branches',
                width: 'w-[180px]'
              }
            ]
          }}
        />
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded-md border-l-4 border-danger-600">
            {error}
          </div>
        )}

        <Table
          columns={[
            {
              header: 'S.No',
              accessor: 'serial',
              width: '80px',
              render: (_, row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1
            },
            {
              header: 'Email',
              accessor: 'email',
              width: '260px',
              render: (value) => (
                <span className="text-sm text-gray-900" title={value}>
                  {value && value.length > 38 ? `${value.substring(0, 38)}...` : value}
                </span>
              )
            },
            {
              header: 'Region',
              accessor: 'region_name',
              width: '180px',
              render: (value, row) => (
                <span className="text-sm text-gray-900" title={row.region_full_name || value}>
                  {row.region_full_name || value || '-'}
                </span>
              )
            },
            {
              header: 'Branch',
              accessor: 'branch_name',
              width: '200px',
              render: (value, row) => (
                <span className="text-sm text-gray-900" title={row.branch_full_name || value}>
                  {row.branch_full_name || value || '-'}
                </span>
              )
            },
            {
              header: 'Created',
              accessor: 'created_at',
              width: '120px',
              render: (value) => formatDateDDMMYYYY(value),
            },
            {
              header: 'Status',
              accessor: 'status',
              width: '100px',
              align: 'center',
              render: (value, row) => (
                <StatusToggle
                  status={value}
                  onChange={(newStatus) => handleStatusToggle(row, newStatus)}
                  loading={updatingStatus[row.id]}
                  disabled={!permissions.can_edit}
                />
              )
            },
            {
              header: 'Actions',
              accessor: 'actions',
              width: '100px',
              align: 'center',
              actions: [
                ...(permissions.can_edit ? [{ type: 'edit', onClick: handleEdit }] : []),
                ...(permissions.can_delete ? [{ type: 'delete', onClick: (row) => handleDelete(row.id) }] : [])
              ]
            },
          ]}
          data={permissions.can_list ? senderEmails : []}
          onRowClick={permissions.can_view ? handleView : undefined}
          emptyMessage={!permissions.can_list ? "Permission Denied - You don't have permission to view this data" : 'No sender addresses found'}
          loading={loading}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </Card>
    </div>
  );
};

export default SenderEmailManager;
